<template>
    <div
        class="flex gap-2 w-full overflow-hidden"
    >
        <redesign-tag
            v-for="(tag, i) in tagsContent"
            :key="i + call.ivrid"
            :color="tag.color"
            :animated="tag.animated"
            :tag-content="tag.tagContent"
            :icon-size="dynamicSizes.iconSize"
            :text-size="dynamicSizes.textSize"
            class="gap-2 text-inherit"
            :uppercase="false"
        />
    </div>
</template>
<script setup lang="ts">
import { useTimer } from '@/composables/useTimer'
import { computed, ref, watch } from 'vue'
import { redesignCallTypeIcons } from '@/enum/colors'
import { activeLayoutThresholdConfig, isVisualAlertDisabled } from '@/composables/globalData/useLayouts'
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import { Call } from '@/types/extension'

const TIMER_ICON = 'Timer'

const callTagColor = ref('')
const callTagIcon = ref('')
const onHold = ref(false)

const props = defineProps<{
    call: Call
    textSize: string
}>()

const callStartedTime = computed(() => {
    return props.call.callStarted_UTC_CLIENT
})

const thresholdConfig = computed(() => {
    return activeLayoutThresholdConfig.value
})

const ActiveCallLimit = computed(() => thresholdConfig.value.ActiveCallLimit)
const ActiveCallLimitColor = computed(() => thresholdConfig.value.ActiveCallLimitColor)
const ActiveCallWarning = computed(() => thresholdConfig.value.ActiveCallWarning)
const ActiveCallWarningColor = computed(() => thresholdConfig.value.ActiveCallWarningColor)
const HoldTimeLimit = computed(() => thresholdConfig.value.HoldTimeLimit)
const HoldTimeWarning = computed(() => thresholdConfig.value.HoldTimeWarning)
const HoldTimeLimitColor = computed(() => thresholdConfig.value.HoldTimeLimitColor)
const HoldTimeWarningColor = computed(() => thresholdConfig.value.HoldTimeWarningColor)


const holdTagColor = computed(() => {
    return +timerHoldState.value >= +HoldTimeLimit.value
        ? HoldTimeLimitColor.value
        : +timerHoldState.value >= +HoldTimeWarning.value
            ? HoldTimeWarningColor.value
            : ''
})

const holdStartTime = computed(() => {
    return props.call.holdStarted_UTC_CLIENT || new Date().getTime()
})

const dynamicSizes = computed(() => {
    return {
        textSize: props.textSize,
        iconSize: parseInt(props.textSize)
    }
})

const tagsContent = computed(() => {
    const callInfoTag = {
        color: props.call.callType === 'Incoming' ? redesignCallTypeIcons.incomingColor : redesignCallTypeIcons.outgoingColor,
        animated: false,
        tagContent: [
            {
                iconName: props.call.callType === 'Incoming' ? redesignCallTypeIcons.incoming : redesignCallTypeIcons.outgoing,
                text: props.call.callType
            }
        ]
    }

    const callTag = {
        color: callTagColor.value,
        animated: !!callTagColor.value && !isVisualAlertDisabled.value,
        tagContent: [
            {
                iconName: callTagIcon.value,
                text: timerDisplayTime.value
            }
        ]
    }
    const hold = {
        color: holdTagColor.value,
        animated: !!callTagColor.value && !isVisualAlertDisabled.value,
        tagContent: [
            {
                iconName: 'Pause',
                text: timerHoldTimeDisplay.value
            }
        ]
    }
    return onHold.value ? [ callInfoTag, callTag, hold ] : [ callInfoTag, callTag ]
})

const { startTimer, timerState, timerDisplayTime } = useTimer(false)
const {
    timerDisplayTime: timerHoldTimeDisplay,
    timerState: timerHoldState,
    startTimer: startHoldTimer,
    stopTimer: stopHoldTimer

} = useTimer(false)
startTimer(callStartedTime)

watch(() => props.call.callstatus, (val, oldValue) => {
    if (val === 'Hold') {
        startHoldTimer(holdStartTime)
        onHold.value = true

    } else if (oldValue === 'Hold' && val !== oldValue) {
        stopHoldTimer()
        onHold.value = false
    }
}, { immediate: true })

watch(() => timerState.value, (value) => {
    const newColor = value > Number(ActiveCallLimit.value)
        ? ActiveCallLimitColor.value
        : value > Number(ActiveCallWarning.value)
            ? ActiveCallWarningColor.value
            : ''
    const newIcon = newColor ? TIMER_ICON : ''

    if (newIcon && newIcon !== callTagIcon.value) callTagIcon.value = newIcon
    if (newColor && newColor !== callTagColor.value) callTagColor.value = newColor
})
</script>


<style scoped lang="scss">

</style>
